<template>
  <el-form
    :model="feedFormat"
    label-position="top"
    :class="{ desktop: isDesktop }"
    @submit.prevent
  >
    <el-form-item :error="errors.name" label="Name" required>
      <el-input v-model="feedFormat.name" type="text" />
    </el-form-item>
    <el-form-item label="Ad type" :error="errors.type_ids" required>
      <AdTypeSelect
        v-model="feedFormat.type_ids"
        :disabled="disabled"
        :radio-view="true"
        :allowed-types="[
          AdType.Popunder,
          AdType.Interstitial,
          AdType.InpagePush
        ]"
      />
    </el-form-item>
    <el-form-item label="Protocol" required>
      <ProtocolSelector v-model="feedFormat.type" :disabled="disabled" />
    </el-form-item>
    <el-form-item>
      <PricingSelector
        v-model="feedFormat.rate_in_cpm"
        :cpc="feedFormat.type_ids === AdType.InpagePush"
      />
    </el-form-item>
    <el-form-item
      :error="errors.response_sample"
      label="Response sample"
      required
    >
      <div class="response-instruction">
        <div>
          1. Enter a sample response from your server in XML/JSON format in the
          "Response Sample" field.
        </div>
        <div>
          2. In each field below, enter an XPath/JSONPath query to obtain the
          desired data from your server's response.
        </div>
        <div>
          3. Click "Check" to verify that the sample was parsed successfully.
        </div>
        <div>4. Save.</div>
      </div>
      <el-input
        v-model="feedFormat.response_sample"
        :autosize="{ minRows: 2, maxRows: 4 }"
        type="textarea"
        :disabled="disabled"
      />
    </el-form-item>
    <template v-if="isStaff">
      <el-form-item :error="errors.rate_xpath" label="Bid" required>
        <el-input
          v-model="feedFormat.rate_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item :error="errors.url_xpath" label="Click URL" required>
        <el-input
          v-model="feedFormat.url_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item
        v-if="feedFormat.type_ids === AdType.InpagePush"
        :error="errors.icon_xpath"
        label="Icon"
      >
        <el-input
          v-model="feedFormat.icon_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item
        v-if="feedFormat.type_ids === AdType.InpagePush"
        :error="errors.image_xpath"
        label="Image"
      >
        <el-input
          v-model="feedFormat.image_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item
        v-if="feedFormat.type_ids === AdType.InpagePush"
        :error="errors.title_xpath"
        label="Title"
        required
      >
        <el-input
          v-model="feedFormat.title_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item
        v-if="feedFormat.type_ids === AdType.InpagePush"
        :error="errors.description_xpath"
        label="Description"
      >
        <el-input
          v-model="feedFormat.description_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item
        :error="errors.impression_url_xpath"
        label="Billable impression URL"
      >
        <el-input
          v-model="feedFormat.impression_url_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
      <el-form-item :error="errors.win_notify_url_xpath" label="Win URL">
        <el-input
          v-model="feedFormat.win_notify_url_xpath"
          type="text"
          :disabled="disabled"
        />
      </el-form-item>
    </template>
    <el-form-item class="check-response">
      <el-button
        type="primary"
        plain
        :disabled="disabled || checkLoading"
        :icon="checkLoading ? 'LoadingElementIcon' : 'RefreshElementIcon'"
        @click="checkResponse"
      >
        Check
      </el-button>
    </el-form-item>
    <el-form-item v-if="checkedResponseItems.length" label="Parsing result">
      <div class="parsing-result">
        <div
          v-for="[key, value] in checkedResponseItems"
          :key="key"
          class="parsing-result__row"
        >
          <span class="parsing-result__key">{{ key }}</span>
          <span class="parsing-result__value">
            {{ value }}
          </span>
        </div>
      </div>
    </el-form-item>
    <el-form-item class="buttons">
      <el-button
        type="primary"
        plain
        :disabled="loading"
        :icon="loading ? 'LoadingElementIcon' : ''"
        @click.prevent="onSubmit(false)"
      >
        {{ actionText }}
      </el-button>
      <router-link to="/integrations" class="el-button is-plain">
        <span>Cancel</span>
      </router-link>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import ProtocolSelector from '../../components/ProtocolSelector.vue'
import PricingSelector from '../../components/PricingSelector.vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'

import { FeedFormatValidationErrorsKey, AdType } from '@/api/types'
import { useFeedFormatsStore } from '@/apps/traforama/store/feedFormats'
import useValidation from '@/hooks/useValidation'
import useResize from '@/hooks/useResize'
import { useUserStore } from '@/store/modules/user'
import { safeSubmitProcessing } from '@/utils/helpers'

export default defineComponent({
  name: 'IntegrationForm',
  components: {
    AdTypeSelect,
    ProtocolSelector,
    PricingSelector
  },
  props: {
    modelValue: {
      required: true,
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    }
  },
  setup(props, context) {
    const feedFormatsStore = useFeedFormatsStore()
    const userStore = useUserStore()

    const { isStaff } = userStore
    const { validationErrors, setValidationErrors } = useValidation(
      FeedFormatValidationErrorsKey
    )
    const errors = validationErrors()
    const feedFormat = ref()
    feedFormat.value = { ...props.modelValue }

    const disabled = computed(() => {
      return userStore.staff ? false : feedFormat.value.id !== undefined
    })

    const checkedResponse = computed(() => {
      return feedFormatsStore.checkedResponse
    })

    const checkedResponseItems = computed(() => {
      return Object.entries(checkedResponse.value).filter(([, value]) => value)
    })

    const checkLoading = ref(false)

    const checkResponse = async () => {
      await safeSubmitProcessing(
        checkLoading,
        () => feedFormatsStore.CheckResponse(feedFormat.value),
        () => {},
        (err) => setValidationErrors(err, errors)
      )
    }

    const { isDesktop } = useResize()

    const onSubmit = function (withReview?: boolean) {
      context.emit('submit', {
        feedFormat: feedFormat.value,
        withReview: withReview
      })
    }

    return {
      onSubmit,
      disabled,
      errors,
      isDesktop,
      AdType,
      feedFormat,
      checkResponse,
      checkLoading,
      checkedResponse,
      checkedResponseItems,
      isStaff
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.el-form-item) {
  margin-bottom: 10px;

  .el-form-item__label {
    padding: 0;
  }
}

.buttons {
  margin-top: 20px;
}

.response-instruction {
  width: 100%;
  margin-bottom: 8px;
  padding: 10px 12px;
  color: var(--el-text-color-primary);
  background-color: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color-light);
  border-radius: 4px;
  line-height: 1.5;
}

.check-response {
  margin-top: 4px;
}

.parsing-result {
  width: 100%;
  border: 1px solid var(--el-border-color-light);
  border-radius: 4px;
  overflow: hidden;
}

.parsing-result__row {
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(0, 1fr);
  gap: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--el-border-color-lighter);

  &:last-child {
    border-bottom: none;
  }
}

.parsing-result__key {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.parsing-result__value {
  overflow-wrap: anywhere;
  color: var(--el-text-color-regular);
}
</style>
